/**
 * Product showcase - Figma `6776:42600` (1360 wide, 1003 tall).
 *
 * A head - the title and a short paragraph on the left, a button at the right foot -
 * above a row of two panels: a black column of three figures (283 wide, 32 in from its
 * edge, a figure 104 above the next) and one large product picture (1061 by 765) with a
 * frosted panel of feature lines resting on its right side.
 *
 * It is one section rather than a `stats` and a `product-preview` because neither draws
 * this: `stats` is a row of orange tiles with no picture, and `product-preview` is two
 * equal panels with captions. The figures and the feature lines reuse the shared
 * `stat-item` and `card` components, so there is nothing new for an editor to learn.
 *
 * The panel over the picture is CSS - white at 40% and a 7px backdrop blur - so it frosts
 * whatever picture is behind it. Each line is a white tile with a green Heroicon.
 *
 * On a phone the picture comes first, the feature lines sit under it and the figures
 * follow, none of them overlapping anything.
 */
import { CtaLink, SectionShell, withHighlight } from '../primitives';
import { HeroIcon } from '../../components/ui/HeroIcon';
import { SIZES } from '../../components/ui/Image';
import { MediaFrame, hasMedia } from '../../components/ui/MediaFrame';
import type { ProductShowcaseSection } from '../../types/sections';

export default function ProductShowcase({ data }: { data: ProductShowcaseSection }) {
  const stats = data.stats ?? [];
  const features = (data.features ?? []).filter((feature) => feature?.title);
  const picture = hasMedia(data.media);

  return (
    <SectionShell settings={data.settings} name="product-showcase">
      <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
        <div className="flex max-w-[968px] flex-col gap-10">
          <h2 className="text-h3 text-ink lg:text-h2">{withHighlight(data.heading)}</h2>
          {data.body ? <p className="max-w-[640px] whitespace-pre-line text-h5 text-ink">{data.body}</p> : null}
        </div>
        {data.cta ? <div className="shrink-0"><CtaLink value={data.cta} /></div> : null}
      </div>

      {stats.length || picture ? (
        <div className="mt-14 grid gap-4 lg:grid-cols-[283fr_1061fr]">
          {stats.length ? (
            <div className="order-2 flex flex-col gap-10 rounded-l bg-black px-8 py-12 text-white lg:order-none lg:gap-[104px]">
              {stats.map((stat, i) => (
                <div key={i} className="flex flex-col gap-3">
                  <p className="text-h1 font-light leading-none">{stat.value}</p>
                  <p className="text-body-md">{stat.label}</p>
                </div>
              ))}
            </div>
          ) : null}

          {picture ? (
            <div className="relative flex min-h-[320px] flex-col justify-end gap-4 overflow-hidden rounded-l bg-surface-sunken p-4 lg:block lg:aspect-[1061/765] lg:min-h-0 lg:p-0">
              <div className="absolute inset-0">
                <MediaFrame media={data.media} sizes={SIZES.full} title={data.heading} />
              </div>

              {features.length ? (
                <ul className="relative m-0 flex list-none flex-col gap-2 rounded-m bg-white/40 p-2 backdrop-blur-[7px] lg:absolute lg:right-10 lg:top-[33%] lg:w-[285px]">
                  {features.map((feature, i) => (
                    <li key={i} className="flex h-[52px] items-center gap-3.5 rounded-s bg-surface px-4 text-body-md text-ink-strong">
                      {feature.icon ? <HeroIcon name={feature.icon} size={24} className="shrink-0 text-green-500" /> : null}
                      {feature.title}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </SectionShell>
  );
}
